import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import InputGroup, { InputGroupInput } from '../InputGroup/InputGroup';
import NumberInput from './NumberInput';
import type { InputSizeVariant } from './RawInput';

export interface IncrementableNumberInputProps extends Omit<
  InputGroupInput,
  'type' | 'value' | 'onChange'
> {
  sizeVariant?: InputSizeVariant;
  wrapperClassName?: string;
  value: number | null;
  onChange?: (quantity: number | null) => void;
  increment?: number;
  min?: number;
  max?: number;
}

const validateNewQuantity = (
  newQuantity: number,
  { min, max }: { min?: number; max?: number }
) => {
  if (typeof min === 'number' && newQuantity < min) {
    return 'TOO_LOW';
  }

  if (typeof max === 'number' && newQuantity > max) {
    return 'TOO_HIGH';
  }

  return 'VALID';
};

const IncrementableNumberInput: FC<IncrementableNumberInputProps> = (props) => {
  const {
    sizeVariant,
    wrapperClassName,
    value,
    increment = 1,
    min,
    max,
    onChange,
    disabled,
    ...rest
  } = props;

  const setClampedValue = (inputValue: number | null) => {
    const newValue = inputValue ?? 0;

    switch (
      validateNewQuantity(newValue, {
        min,
        max,
      })
    ) {
      case 'TOO_LOW':
        if (typeof min === 'number') {
          onChange?.(min);
        }
        break;
      case 'TOO_HIGH':
        if (typeof max === 'number') {
          onChange?.(max);
        }
        break;
      case 'VALID':
        onChange?.(newValue);
        break;
    }
  };

  const handleIncrement = (increment: number) => () => {
    const newValue = (value ?? 0) + increment;
    setClampedValue(newValue);
  };

  return (
    <InputGroup className={wrapperClassName} sizeVariant={sizeVariant}>
      <InputGroup.Button
        variant={'white'}
        onClick={handleIncrement(-increment)}
        disabled={
          disabled ||
          (typeof min === 'number' && value !== null && value <= min)
        }
        aria-label={`Decrement by ${increment}`}
      >
        <UntitledIcon name="minus.3" className="h-4 w-4" />
      </InputGroup.Button>

      <NumberInput
        inputClassName="text-center"
        as={InputGroup.Input}
        value={value}
        onChange={setClampedValue}
        min={min}
        max={max}
        disabled={disabled}
        {...rest}
      />

      <InputGroup.Button
        variant={'white'}
        onClick={handleIncrement(increment)}
        disabled={
          disabled ||
          (typeof max === 'number' && value !== null && value >= max)
        }
        aria-label={`Increment by ${increment}`}
      >
        <UntitledIcon name="plus.3" className="h-4 w-4" />
      </InputGroup.Button>
    </InputGroup>
  );
};

export default IncrementableNumberInput;
